/**
 * Native platform bootstrap — runs once when the Vue app mounts.
 *
 * On native (Capacitor), uses the global `window.Capacitor.Plugins` object
 * that Capacitor's native bridge injects. This is more reliable than ESM
 * imports of `@capacitor/*` packages, which can be broken by aggressive
 * tree-shaking or CJS/ESM interop issues during bundling.
 *
 * Safety: a hard fallback hides the splash after 2.5s even if any of the
 * native calls throw or hang.
 *
 * Call once from main.ts after app.mount(), not during SSR.
 */
import { isNative, platformName } from "./platform";

interface CapacitorWindow extends Window {
  Capacitor?: {
    isNativePlatform: () => boolean;
    getPlatform: () => "ios" | "android" | "web";
    Plugins: Record<string, any>;
  };
}

function caps(): CapacitorWindow["Capacitor"] {
  return (window as CapacitorWindow).Capacitor;
}

export async function bootstrapNative(): Promise<void> {
  if (!isNative()) return;

  const cap = caps();
  if (!cap) {
    console.warn("Capacitor global not found");
    return;
  }

  // Schedule a guaranteed splash hide even if everything below fails.
  const failsafeHide = setTimeout(() => {
    void hideSplashSafe();
  }, 2500);

  try {
    // Hide splash with a small delay so Vue paints first frame
    setTimeout(() => {
      void hideSplashSafe();
    }, 300);

    // Status bar — read from the bridge global, not ESM imports
    const StatusBar = cap.Plugins?.StatusBar;
    if (StatusBar) {
      // Style.Dark = "DARK" (Capacitor encodes enums as strings on the wire)
      await StatusBar.setStyle({ style: "DARK" }).catch(() => {});
      if (platformName() === "android") {
        await StatusBar.setBackgroundColor({ color: "#0A0E1A" }).catch(() => {});
      }
      await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    }
    // Keyboard plugin doesn't need any method call here — its config in
    // capacitor.config.ts (resize: "body") activates the body-resize
    // behavior automatically when the plugin is present in Gradle.
  } catch (e) {
    console.warn("Native bootstrap warning:", e);
  } finally {
    clearTimeout(failsafeHide);
    void hideSplashSafe();
  }
}

/** Try to hide the splash via the Capacitor bridge, swallowing any error.
 * Idempotent. Safe to call multiple times. */
async function hideSplashSafe(): Promise<void> {
  const cap = caps();
  const SplashScreen = cap?.Plugins?.SplashScreen;
  if (!SplashScreen) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    // Already hidden or bridge error — fine
  }
}
